import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getContest, createContest, updateContest, deleteContest } from '../../lib/contests';

const TRANSLATION_LANGUAGE_OPTIONS = [
  { value: 'vi', label: 'Vietnamese' },
  { value: 'es', label: 'Spanish' },
  { value: 'id', label: 'Bahasa Indonesia' },
  { value: 'tl', label: 'Filipino' },
  { value: 'hi', label: 'हिन्दी' },
  { value: 'pt', label: 'Português (Brasil)' },
  { value: 'th', label: 'ภาษาไทย' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Français' },
  { value: 'it', label: 'Italiano' },
];

export default function ContestEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();
  const isNew = id === 'new';

  // Date helpers
  const formatDateTimeLocal = (ms: number) => {
    const d = new Date(ms);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const parseDateTimeLocal = (value: string) => {
    return new Date(value).getTime();
  };

  const [form, setForm] = useState({
    type: 'contest' as 'contest' | 'discount',
    title: '',
    description: '',
    pixRequired: 0,
    startAt: Date.now() + 3600_000, // 1 hour from now
    endAt: Date.now() + 7*24*3600_000, // 7 days from now
    isActive: true,
    partnerName: '',
    partnerLogoUrl: '',
    discountCode: '',
    externalUrl: '',
    maxEntries: null as number | null,
    allowMultipleEntries: false,
    targetLanguage: 'all',
    imageUrl: '',
    i18n: { title: {} as Record<string, string>, description: {} as Record<string, string> },
  });
  const [activeTab, setActiveTab] = useState<'details' | 'translations'>('details');
  const [newTranslationLang, setNewTranslationLang] = useState('');
  const [newTranslationTitle, setNewTranslationTitle] = useState('');
  const [newTranslationDescription, setNewTranslationDescription] = useState('');
  const [editingTranslationLang, setEditingTranslationLang] = useState<string | null>(null);
  const [editingTranslationTitle, setEditingTranslationTitle] = useState('');
  const [editingTranslationDescription, setEditingTranslationDescription] = useState('');
  const [file, setFile] = useState<File | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isNew && id) {
      getContest(id).then((c: any) => {
        if (!c) {
          alert('Contest not found');
          navigate('/contests');
          return;
        }
        
        // Convert Firestore Timestamps to milliseconds
        const startAt = c.startAt?.toMillis ? c.startAt.toMillis() : (typeof c.startAt === 'number' ? c.startAt : Date.now());
        const endAt = c.endAt?.toMillis ? c.endAt.toMillis() : (typeof c.endAt === 'number' ? c.endAt : Date.now());
        
        setForm({
          type: c.type || 'contest',
          title: c.title || '',
          description: c.description || '',
          pixRequired: c.pixRequired || 0,
          startAt,
          endAt,
          isActive: c.isActive !== false,
          partnerName: c.partnerName || '',
          partnerLogoUrl: c.partnerLogoUrl || '',
          discountCode: c.discountCode || '',
          externalUrl: c.externalUrl || '',
          maxEntries: c.maxEntries ?? null,
          allowMultipleEntries: c.allowMultipleEntries || false,
          targetLanguage: c.targetLanguage ?? 'all',
          imageUrl: c.imageUrl || '',
          i18n: c.i18n || { title: {}, description: {} },
        });
      });
    }
  }, [id, isNew, navigate]);

  async function onSave() {
    // Validation
    if (!form.title.trim()) {
      alert('Title is required');
      return;
    }
    if (!form.description.trim()) {
      alert('Description is required');
      return;
    }
    if (form.pixRequired <= 0) {
      alert('PIX Required must be greater than 0');
      return;
    }
    if (form.startAt >= form.endAt) {
      alert('End date must be after start date');
      return;
    }
    if (form.type === 'discount' && !form.discountCode?.trim()) {
      alert('Discount Code is required for discounts');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        type: form.type,
        title: form.title.trim(),
        description: form.description.trim(),
        pixRequired: Number(form.pixRequired),
        startAt: Number(form.startAt),
        endAt: Number(form.endAt),
        isActive: form.isActive,
        partnerName: form.partnerName?.trim() || undefined,
        partnerLogoUrl: form.partnerLogoUrl?.trim() || undefined,
        discountCode: form.discountCode?.trim() || undefined,
        externalUrl: form.externalUrl?.trim() || undefined,
        maxEntries: form.maxEntries ?? undefined, // Use undefined instead of null for optional fields
        allowMultipleEntries: form.allowMultipleEntries || false,
        targetLanguage: form.targetLanguage,
        imageUrl: form.imageUrl?.trim() || undefined,
        i18n: form.i18n || { title: {}, description: {} },
        ...(isNew && auth.currentUser ? { createdByUid: auth.currentUser.uid } : {}),
      };

      if (isNew) {
        const newId = await createContest(payload, file);
        navigate(`/contests/${newId}`);
      } else if (id) {
        await updateContest(id, payload, file);
        alert('Contest updated successfully');
      }
    } catch (error: any) {
      console.error('Error saving contest:', error);
      alert(`Failed to save contest: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }

  async function onDelete() {
    if (!id || isNew) return;
    if (!window.confirm(`Are you sure you want to delete "${form.title}"? This action cannot be undone.`)) return;
    
    setLoading(true);
    try {
      await deleteContest(id);
      navigate('/contests');
    } catch (error: any) {
      console.error('Error deleting contest:', error);
      alert(`Failed to delete contest: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }

  function updateField(field: string, value: any) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  function addTranslation() {
    if (!newTranslationLang || !newTranslationTitle || !newTranslationDescription) return;
    setForm((f) => ({
      ...f,
      i18n: {
        title: { ...(f.i18n?.title || {}), [newTranslationLang]: newTranslationTitle },
        description: { ...(f.i18n?.description || {}), [newTranslationLang]: newTranslationDescription },
      }
    }));
    setNewTranslationLang('');
    setNewTranslationTitle('');
    setNewTranslationDescription('');
  }

  function updateTranslation() {
    if (!editingTranslationLang) return;
    setForm((f) => ({
      ...f,
      i18n: {
        title: { ...(f.i18n?.title || {}), [editingTranslationLang]: editingTranslationTitle },
        description: { ...(f.i18n?.description || {}), [editingTranslationLang]: editingTranslationDescription },
      }
    }));
    setEditingTranslationLang(null);
  }

  function deleteTranslation(lang: string) {
    setForm((f) => {
      const newTitle = { ...(f.i18n?.title || {}) };
      const newDesc = { ...(f.i18n?.description || {}) };
      delete newTitle[lang];
      delete newDesc[lang];
      return { ...f, i18n: { title: newTitle, description: newDesc } };
    });
  }

  return (
    <div className="p-6">
      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={() => navigate('/contests')}
          className="px-3 py-2 border rounded hover:bg-gray-50 flex items-center gap-2"
        >
          ← Back
        </button>
        <h1 className="text-2xl font-bold">{isNew ? 'New Contest or Discount' : 'Edit Contest or Discount'}</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl">
        <div className="md:col-span-2 border rounded p-6 space-y-6">
          <div className="flex border-b mb-4">
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'details' ? 'border-b-2 border-black' : 'text-gray-500'}`}
              onClick={() => setActiveTab('details')}
            >
              Details
            </button>
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'translations' ? 'border-b-2 border-black' : 'text-gray-500'}`}
              onClick={() => setActiveTab('translations')}
            >
              Translations
            </button>
          </div>
          {activeTab === 'details' && (
            <>
        {/* Type */}
        <label className="block">
          <div className="font-medium mb-1">Type *</div>
          <select
            className="w-full border rounded px-3 py-2"
            value={form.type}
            onChange={(e) => updateField('type', e.target.value as 'contest' | 'discount')}
          >
            <option value="contest">Contest</option>
            <option value="discount">Discount</option>
          </select>
        </label>

        {/* Title */}
        <label className="block">
          <div className="font-medium mb-1">Title *</div>
          <input
            type="text"
            className="w-full border rounded px-3 py-2"
            value={form.title}
            onChange={(e) => updateField('title', e.target.value)}
            placeholder="Enter contest or discount title"
          />
        </label>

        {/* Description */}
        <label className="block">
          <div className="font-medium mb-1">Description *</div>
          <textarea
            className="w-full border rounded px-3 py-2"
            rows={4}
            value={form.description}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder="Enter description"
          />
        </label>

        {/* PIX Required */}
        <label className="block">
          <div className="font-medium mb-1">PIX Required *</div>
          <input
            type="number"
            min="1"
            className="w-full border rounded px-3 py-2"
            value={form.pixRequired}
            onChange={(e) => updateField('pixRequired', parseInt(e.target.value) || 0)}
            placeholder="Enter PIX amount required"
          />
        </label>

        <label className="block">
          <div className="font-medium">Target Language</div>
          <select className="mt-1 border rounded px-3 py-2 w-full"
            value={form.targetLanguage ?? 'all'} onChange={e=>updateField('targetLanguage', e.target.value)}>
            <option value="all">All Languages (Default)</option>
            <option value="vi">Vietnamese</option>
            <option value="es">Spanish</option>
            <option value="id">Bahasa Indonesia</option>
            <option value="tl">Filipino</option>
            <option value="hi">हिन्दी</option>
            <option value="pt">Português (Brasil)</option>
            <option value="th">ภาษาไทย</option>
            <option value="de">Deutsch</option>
            <option value="fr">Français</option>
            <option value="it">Italiano</option>
          </select>
        </label>

        {/* Partner Name */}
        <label className="block">
          <div className="font-medium mb-1">Partner Name (optional)</div>
          <input
            type="text"
            className="w-full border rounded px-3 py-2"
            value={form.partnerName}
            onChange={(e) => updateField('partnerName', e.target.value)}
            placeholder="e.g., Local Pizza Co"
          />
        </label>

        {/* Partner Logo URL */}
        <label className="block">
          <div className="font-medium mb-1">Partner Logo URL (optional)</div>
          <input
            type="url"
            className="w-full border rounded px-3 py-2"
            value={form.partnerLogoUrl}
            onChange={(e) => updateField('partnerLogoUrl', e.target.value)}
            placeholder="https://example.com/logo.png"
          />
        </label>

        {/* Image Upload */}
        <label className="block">
          <div className="font-medium mb-1">Image</div>
          <input
            type="file"
            accept="image/*"
            className="mt-1 w-full border rounded px-3 py-2"
            onChange={(e) => setFile(e.target.files?.[0])}
          />
          {form.imageUrl && (
            <img 
              alt="Contest preview" 
              src={form.imageUrl} 
              className="mt-2 h-32 rounded border object-cover" 
            />
          )}
          {file && (
            <div className="mt-2 text-sm text-gray-600">
              New image selected: {file.name}
            </div>
          )}
        </label>

        {/* Date/Time Fields */}
        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <div className="font-medium mb-1">Start Date & Time *</div>
            <input
              type="datetime-local"
              className="w-full border rounded px-3 py-2"
              value={formatDateTimeLocal(form.startAt)}
              onChange={(e) => updateField('startAt', parseDateTimeLocal(e.target.value))}
            />
          </label>
          <label className="block">
            <div className="font-medium mb-1">End Date & Time *</div>
            <input
              type="datetime-local"
              className="w-full border rounded px-3 py-2"
              value={formatDateTimeLocal(form.endAt)}
              onChange={(e) => updateField('endAt', parseDateTimeLocal(e.target.value))}
            />
          </label>
        </div>

        {/* Is Active */}
        <label className="block">
          <div className="flex items-center">
            <input
              type="checkbox"
              className="mr-2"
              checked={form.isActive}
              onChange={(e) => updateField('isActive', e.target.checked)}
            />
            <span className="font-medium">Is Active</span>
          </div>
        </label>

        {/* Discount-specific fields */}
        {form.type === 'discount' && (
          <>
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Discount Settings</h3>
              
              <label className="block mb-4">
                <div className="font-medium mb-1">Discount Code *</div>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  value={form.discountCode}
                  onChange={(e) => updateField('discountCode', e.target.value)}
                  placeholder="Enter discount code"
                />
              </label>

              <label className="block">
                <div className="font-medium mb-1">External URL (optional)</div>
                <input
                  type="url"
                  className="w-full border rounded px-3 py-2"
                  value={form.externalUrl}
                  onChange={(e) => updateField('externalUrl', e.target.value)}
                  placeholder="https://partner-website.com/redeem"
                />
              </label>
            </div>
          </>
        )}

        {/* Contest-specific fields */}
        {form.type === 'contest' && (
          <>
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Contest Settings</h3>
              
              <label className="block mb-4">
                <div className="font-medium mb-1">Max Entries (optional)</div>
                <input
                  type="number"
                  min="1"
                  className="w-full border rounded px-3 py-2"
                  value={form.maxEntries || ''}
                  onChange={(e) => updateField('maxEntries', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="Leave empty for unlimited"
                />
              </label>

              <label className="block">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={form.allowMultipleEntries}
                    onChange={(e) => updateField('allowMultipleEntries', e.target.checked)}
                  />
                  <span className="font-medium">Allow Multiple Entries</span>
                </div>
              </label>
            </div>
          </>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t">
          <button
            onClick={onSave}
            disabled={loading}
            className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50"
          >
            {loading ? 'Saving…' : 'Save'}
          </button>
          {!isNew && (
            <button
              onClick={onDelete}
              disabled={loading}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
            >
              Delete
            </button>
          )}
          <button
            onClick={() => navigate('/contests')}
            disabled={loading}
            className="px-4 py-2 border rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
            </>
          )}
          {activeTab === 'translations' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Add translated titles and descriptions for non-English languages. Users will only see this challenge/contest if a translation exists for their language.</p>

              {TRANSLATION_LANGUAGE_OPTIONS.filter(opt =>
                form.i18n?.title?.[opt.value] || form.i18n?.description?.[opt.value]
              ).map(opt => (
                <div key={opt.value} className="border rounded p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{opt.label}</span>
                    <div className="flex gap-2">
                      <button type="button" className="text-sm text-blue-600 hover:underline"
                        onClick={() => {
                          setEditingTranslationLang(opt.value);
                          setEditingTranslationTitle(form.i18n?.title?.[opt.value] || '');
                          setEditingTranslationDescription(form.i18n?.description?.[opt.value] || '');
                        }}>Edit</button>
                      <button type="button" className="text-sm text-red-600 hover:underline"
                        onClick={() => deleteTranslation(opt.value)}>Delete</button>
                    </div>
                  </div>
                  {editingTranslationLang === opt.value ? (
                    <div className="space-y-2">
                      <input className="border rounded px-3 py-2 w-full" placeholder="Translated title"
                        value={editingTranslationTitle} onChange={e => setEditingTranslationTitle(e.target.value)} />
                      <textarea className="border rounded px-3 py-2 w-full min-h-[60px]" placeholder="Translated description"
                        value={editingTranslationDescription} onChange={e => setEditingTranslationDescription(e.target.value)} />
                      <div className="flex gap-2">
                        <button type="button" className="px-3 py-1 bg-black text-white rounded text-sm"
                          onClick={updateTranslation}>Save</button>
                        <button type="button" className="px-3 py-1 border rounded text-sm"
                          onClick={() => setEditingTranslationLang(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm"><span className="font-medium">Title:</span> {form.i18n?.title?.[opt.value]}</p>
                      <p className="text-sm"><span className="font-medium">Description:</span> {form.i18n?.description?.[opt.value]}</p>
                    </div>
                  )}
                </div>
              ))}

              <div className="border rounded p-3 space-y-2">
                <div className="font-medium text-sm">Add Translation</div>
                <select className="border rounded px-3 py-2 w-full"
                  value={newTranslationLang} onChange={e => setNewTranslationLang(e.target.value)}>
                  <option value="">Select language...</option>
                  {TRANSLATION_LANGUAGE_OPTIONS.filter(opt =>
                    !form.i18n?.title?.[opt.value]
                  ).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <input className="border rounded px-3 py-2 w-full" placeholder="Translated title"
                  value={newTranslationTitle} onChange={e => setNewTranslationTitle(e.target.value)} />
                <textarea className="border rounded px-3 py-2 w-full min-h-[60px]" placeholder="Translated description"
                  value={newTranslationDescription} onChange={e => setNewTranslationDescription(e.target.value)} />
                <button type="button" className="px-3 py-1 bg-black text-white rounded text-sm"
                  onClick={addTranslation}
                  disabled={!newTranslationLang || !newTranslationTitle || !newTranslationDescription}>
                  Add Translation
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

